import { lazy, Suspense, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import { Download, Plus } from "lucide-react";
import { useAuth } from "@fox1ck-org/auth/react";
import {
  ActiveFilters,
  Badge,
  Button,
  ConfirmModal,
  COUNTRY_CODES,
  FacetFilter,
  PageHeader,
  ProgressBarCell,
  SearchField,
  SegmentedControl,
  geoName,
  type Facet,
  type FacetFilterValue,
} from "@fox1ck-org/ui";
import { toast } from "@fox1ck-org/ui/toast";
import { OfferQuickView, OfferStatus, type Offer, type OfferNumbers } from "@gen/tracker/v1/offer_pb";
import type { PayoutModel } from "@gen/tracker/v1/affiliate_network_pb";

import { ListView } from "@/components/list-view";
import { WriteOnly } from "@/components/auth/write-only";
import { OfferEditor } from "@/components/offers/offer-editor";
import { OFFER_TABS, type OfferTab } from "@/components/offers/offer-card";
import { OFFER_STATUS_VIEW, PAYOUT_MODELS, modelCode, money } from "@/components/offers/labels";
import { OfferEmpty } from "@/components/offers/offer-empty";
import { useT, type TKey } from "@/lib/i18n";
import { DASH, formatInt, formatMoney, formatPct } from "@/lib/format";
import { useCrumbTail } from "@/lib/crumb";
import { carryPeriod } from "@/lib/route-tab";
import { csvEscape, triggerCsvDownload } from "@/lib/csv";
import { useCanWrite, useTrackerRoles } from "@/lib/permissions";
import { useTableParams } from "@/lib/hooks/use-table-params";
import { useDebounced } from "@/lib/hooks/use-debounced";
import { listSort } from "@/lib/services/catalog";
import { statsRowToNumbers } from "@/lib/services/reporting-schema-service";
import { useAllAffiliateNetworks } from "@/lib/services/network-service";
import {
  duplicateOffer,
  useArchiveOffer,
  useGetOffer,
  useListOffers,
  useOfferLibrary,
  useOfferNumbers,
} from "@/lib/services/offer-service";

// The card pulls in the charts: it loads when an offer is opened.
const OfferCard = lazy(() => import("@/components/offers/offer-card").then((m) => ({ default: m.OfferCard })));

/**
 * Офферы (boards OfferList, OfferEmpty; the card and the editor are drawers
 * over it — OfferCard, OfferEditor, OfferEditorErrors). The address says
 * everything: /library/offers[/:id[/:tab]], `new` and `edit` open the editor,
 * the list's view, filters, search, sort and page are search params, and the
 * numbers are the Top bar's period (ListOfferNumbers over the page).
 *
 * Not drawn, on purpose: «+ Сохранить вид» (saved views have no offers
 * surface), «сортировка: лиды за период» (ListOffers sorts by name, status,
 * created only), «Импорт из CSV» (no import RPC).
 */

const VIEWS = ["active", "capped", "silent", "mine", "archived"] as const;
// The quick view's own key — the campaign list's too. `view` is the hooks'
// reserved key for a saved report view; writing it through setFilters throws.
const VIEW_KEY = "qv";
type View = (typeof VIEWS)[number];
const VIEW_LABEL: Record<View, TKey> = {
  active: "u6.view.active",
  capped: "u6.view.capped",
  silent: "u6.view.silent",
  mine: "u6.view.mine",
  archived: "u6.view.archived",
};
const OFFER_SORT: Record<string, string> = { name: "name", status: "status" };
const STATUS_FILTER: Record<string, OfferStatus> = { active: OfferStatus.ACTIVE, paused: OfferStatus.PAUSED, capped: OfferStatus.CAPPED };

const csv = (s: string) => (s ? s.split(",").filter(Boolean) : []);

export default function OffersPage() {
  const t = useT();
  const navigate = useNavigate();
  const { search } = useLocation();
  const [searchParams] = useSearchParams();
  const { id: routeId, tab: routeTab } = useParams();
  const { user } = useAuth();
  const canWrite = useCanWrite();
  const { isSelf } = useTrackerRoles();
  const params = useTableParams({ defaultSort: [{ id: "name", desc: false }] });
  const view = (VIEWS as readonly string[]).includes(params.getFilter(VIEW_KEY)) ? (params.getFilter(VIEW_KEY) as View) : "active";
  const filter: FacetFilterValue = {
    network: csv(params.getFilter("network")),
    status: csv(params.getFilter("status")),
    country: csv(params.getFilter("country")),
    model: csv(params.getFilter("model")),
  };
  const [openFacet, setOpenFacet] = useState<string | null>(null);
  const [archiving, setArchiving] = useState<Offer | null>(null);

  const { data: networksRes } = useAllAffiliateNetworks();
  const networks = useMemo(() => networksRes?.items ?? [], [networksRes]);
  const networkById = useMemo(() => new Map(networks.map((n) => [n.id, n])), [networks]);

  const q = useDebounced(params.search.trim());
  const mine = params.scope === "mine" || view === "mine";
  // The tabs count in the top bar's scope — the same owner_ids the table
  // asks with — so «Работают 12» under «Мои» is the table's 12, not the
  // workspace's. The «Мои» tab itself narrows the table, not its siblings.
  const library = useOfferLibrary(user?.sub, params.scope === "mine" && user?.sub ? [user.sub] : []);
  // The two attention tiles are server sets: the list asks for the tile by
  // name, so its total is the tile's count — no ids travel, no ceiling.
  const quickView = view === "capped" ? OfferQuickView.CAPPED_TODAY : view === "silent" ? OfferQuickView.WITHOUT_LEADS_7D : undefined;
  const statuses = (filter.status as string[]).map((s) => STATUS_FILTER[s]).filter((s): s is OfferStatus => s !== undefined);
  const list = useListOffers(
    {
      search: q,
      sort: listSort(params.sorting, OFFER_SORT),
      limit: params.pageSize,
      offset: params.pageIndex * params.pageSize,
      networkId: (filter.network as string[]).length === 1 ? (filter.network as string[])[0] : undefined,
      statuses: view === "archived" ? [OfferStatus.ARCHIVED] : statuses.length ? statuses : viewStatuses(view),
      countries: filter.country as string[],
      payoutModels: (filter.model as string[]).map((m) => Number(m) as PayoutModel),
      ownerIds: mine && user?.sub ? [user.sub] : undefined,
      quickView,
    },
  );
  const offers = useMemo(() => list.data?.items ?? [], [list.data]);
  const numbers = useOfferNumbers({
    ids: offers.map((o) => o.id),
    range: { from: params.from, to: params.to },
    timezone: params.tz,
    basis: params.basis,
  });
  const byOffer = useMemo(() => new Map((numbers.data?.items ?? []).map((n) => [n.offerId, n])), [numbers.data]);

  // The drawer the address names.
  const creating = routeId === "new";
  const openId = routeId && !creating ? routeId : undefined;
  const editing = !!openId && routeTab === "edit";
  const tab: OfferTab = (OFFER_TABS as readonly string[]).includes(routeTab ?? "") ? (routeTab as OfferTab) : "summary";
  const opened = useGetOffer(openId);
  // «своё»: a company offer is a catalog card to pick from — only his own is
  // his to edit, copy or archive.
  const editable = canWrite && (!isSelf || (!!user?.sub && opened.data?.ownership?.ownerId === user.sub));
  const copyFrom = useGetOffer(creating ? (searchParams.get("copy") ?? undefined) : undefined);
  useCrumbTail(creating ? t("u6.ed.titleNew") : opened.data?.name);
  const base = "/library/offers";
  const go = (path: string) => navigate(`${path}${carryPeriod(search)}`);
  const archive = useArchiveOffer();

  const columns = useMemo<ColumnDef<Offer>[]>(
    () => [
      {
        id: "name",
        header: t("u6.col.offer"),
        cell: ({ row }) => (
          <span className="flex min-w-0 items-baseline gap-2">
            <span className={row.original.status === OfferStatus.PAUSED ? "truncate text-muted-foreground" : "truncate font-medium"}>{row.original.name}</span>
            <span className="shrink-0 font-mono text-xs text-ink-3">#{row.original.id.slice(0, 8)}</span>
          </span>
        ),
      },
      {
        id: "network",
        header: t("u6.col.partner"),
        enableSorting: false,
        size: 140,
        cell: ({ row }) => networkById.get(row.original.networkId)?.name ?? DASH,
      },
      {
        id: "payout",
        header: t("u6.col.payout"),
        enableSorting: false,
        size: 140,
        meta: { align: "right" },
        cell: ({ row }) => (
          <span className="whitespace-nowrap tabular-nums">
            {money(row.original.payout?.amount?.amount, row.original.payout?.amount?.currency)}{" "}
            <span className="text-xs text-muted-foreground">{modelCode(row.original.payout?.model ?? 0)}</span>
          </span>
        ),
      },
      {
        id: "countries",
        header: t("u6.col.countries"),
        enableSorting: false,
        size: 130,
        cell: ({ row }) => (
          <span className="flex flex-nowrap items-center gap-1">
            {row.original.country.slice(0, 3).map((c) => (
              <Badge key={c} variant="outline" size="sm" className="font-mono">
                {c}
              </Badge>
            ))}
            {row.original.country.length > 3 ? <span className="text-xs text-muted-foreground">+{row.original.country.length - 3}</span> : null}
          </span>
        ),
      },
      {
        id: "cap",
        header: t("u6.col.cap"),
        enableSorting: false,
        size: 170,
        meta: { align: "right" },
        cell: ({ row }) => {
          const cap = byOffer.get(row.original.id)?.cap;
          return <ProgressBarCell value={Number(cap?.dailyUsed ?? 0)} max={Number(cap?.dailyLimit ?? 0)} placeholder={t("u6.cap.none")} label={t("u6.col.cap")} />;
        },
      },
      ...numberColumns(t, byOffer),
      {
        id: "status",
        header: t("u6.col.status"),
        size: 150,
        cell: ({ row }) => {
          const reached = byOffer.get(row.original.id)?.cap?.reached;
          const v = reached && row.original.status === OfferStatus.ACTIVE ? OFFER_STATUS_VIEW[OfferStatus.CAPPED] : OFFER_STATUS_VIEW[row.original.status];
          return (
            <Badge variant={v.tone} dot>
              {t(v.label)}
            </Badge>
          );
        },
      },
    ],
    [t, networkById, byOffer],
  );

  const facets: Facet[] = useMemo(
    () => [
      { id: "network", label: t("u6.col.partner"), kind: "options", searchable: true, options: networks.map((n) => ({ value: n.id, label: n.name })) },
      {
        id: "status",
        label: t("u6.col.status"),
        kind: "options",
        options: [
          { value: "active", label: t("u6.status.active") },
          { value: "paused", label: t("u6.status.paused") },
          { value: "capped", label: t("u6.status.capped") },
        ],
      },
      { id: "country", label: t("u6.f.country"), kind: "options", searchable: true, options: COUNTRY_CODES.map((c) => ({ value: c, label: `${c} · ${geoName(c, "country", "ru")}` })) },
      { id: "model", label: t("u6.f.model"), kind: "options", options: PAYOUT_MODELS.map((m) => ({ value: String(m), label: modelCode(m) })) },
    ],
    [t, networks],
  );
  const setFacets = (v: FacetFilterValue) =>
    params.setFilters(Object.fromEntries(facets.map((f) => [f.id, ((v[f.id] as string[] | undefined) ?? []).join(",") || null])));

  const totals = numbers.data?.totals ? statsRowToNumbers(numbers.data.totals) : undefined;
  // «No offers yet» is the workspace's state; under «Мои» an empty count is
  // only the reader's, and the table says so instead.
  const library0 = params.scope !== "mine" && library.data && Number(library.data.active) + Number(library.data.archived) === 0;
  const anyFilter = !!params.search || Object.values(filter).some((v) => (v as string[]).length > 0);

  const exportCsv = () => {
    const head = [t("u6.col.offer"), "id", t("u6.col.partner"), t("u6.col.payout"), t("u6.col.countries"), t("u6.col.leads"), t("u6.col.approve"), t("u6.col.revenue")];
    const lines = offers.map((o) => {
      const n = statsRowToNumbers(byOffer.get(o.id)?.stats);
      return [o.name, o.id, networkById.get(o.networkId)?.name ?? "", `${o.payout?.amount?.amount ?? ""} ${o.payout?.amount?.currency ?? ""}`, o.country.join(" "), String(n.leads ?? ""), String(n.approve_rate ?? ""), String(n.revenue ?? "")]
        .map(csvEscape)
        .join(",");
    });
    triggerCsvDownload([head.map(csvEscape).join(","), ...lines].join("\n"), `offers-${params.from}-${params.to}.csv`);
  };

  if (library0 && !anyFilter && !creating) {
    return (
      <div className="flex h-full min-h-0 flex-col">
        <OfferEmpty hasPartner={networks.length > 0} onCreate={() => go(`${base}/new`)} />
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex shrink-0 flex-col gap-3 px-5 pb-3 pt-4">
        <PageHeader
          title={t("u6.offers.title")}
          subtitle={t("u6.offers.subtitle")}
          actions={
            <>
              <Button variant="secondary" onClick={exportCsv} disabled={offers.length === 0}>
                <Download className="size-4" /> CSV
              </Button>
              <WriteOnly lock={t("u6.offers.new")}>
                <Button variant="primary" onClick={() => go(`${base}/new`)}>
                  <Plus className="size-4" /> {t("u6.offers.new")}
                </Button>
              </WriteOnly>
            </>
          }
        />
        <SegmentedControl
          aria-label={t("u6.view.label")}
          value={view}
          onValueChange={(v) => params.setFilter(VIEW_KEY, v === "active" ? null : v)}
          options={VIEWS.map((v) => ({
            value: v,
            label: t(VIEW_LABEL[v]),
            count: library.data ? viewCount(v, library.data) : undefined,
          }))}
        />
      </div>
      <div className="flex min-h-0 flex-1 flex-col gap-3 px-5 pb-5">
        <ListView
          columns={columns}
          data={offers}
          getRowId={(o) => o.id}
          loading={list.isLoading}
          failure={list.error ? { title: t("u6.offers.loadFailed"), error: list.error, retry: () => void list.refetch() } : undefined}
          onRowClick={(o) => go(`${base}/${o.id}`)}
          rowClassName={(o) => (o.id === openId ? "bg-surface-2" : undefined)}
          manualSorting
          sorting={params.sorting}
          onSortingChange={params.onSortingChange}
          density="compact"
          pagination={{
            pageIndex: params.pageIndex,
            pageSize: params.pageSize,
            totalCount: list.data?.total ?? 0,
            onPageIndexChange: params.setPageIndex,
            onPageSizeChange: params.setPageSize,
            pageSizeOptions: params.pageSizeOptions,
          }}
          totals={
            totals
              ? {
                  name: t("u6.totals.page"),
                  leads: formatInt(totals.leads),
                  approve: formatPct(totals.approve_rate, { digits: 0 }),
                  revenue: formatMoney(totals.revenue),
                  campaigns: formatInt(offers.reduce((s, o) => s + (byOffer.get(o.id)?.campaignCount ?? 0), 0)),
                }
              : undefined
          }
          toolbarLeft={
            <>
              <SearchField value={params.search} onChange={params.setSearch} label={t("u6.offers.search")} placeholder={t("u6.offers.search")} className="w-70" />
              <ActiveFilters
                value={filter}
                facets={facets}
                onRemove={(id) => params.setFilters({ [id]: null })}
                onEdit={(id) => setOpenFacet(id)}
                labels={{ clear: t("u6.f.clear") }}
              />
              <FacetFilter
                facets={facets}
                value={filter}
                onChange={setFacets}
                open={openFacet !== null ? true : undefined}
                onOpenChange={(o) => !o && setOpenFacet(null)}
                activeFacetId={openFacet}
                onActiveFacetChange={setOpenFacet}
                labels={{ trigger: t("u6.f.add"), apply: t("u6.f.apply"), reset: t("u6.f.reset"), noOptions: t("u6.common.nothing"), search: (f) => f, back: t("u6.f.back"), facets: t("u6.f.facets") }}
              />
            </>
          }
          emptyMessage={t("u6.offers.noneMatch")}
        />
      </div>

      {openId && !(editing && editable) ? (
        <Suspense fallback={null}>
          <OfferCard
            offer={opened.data}
            loading={opened.isLoading}
            error={opened.error}
            network={opened.data ? networkById.get(opened.data.networkId) : undefined}
            tab={tab}
            onTab={(next) => go(next === "summary" ? `${base}/${openId}` : `${base}/${openId}/${next}`)}
            onClose={() => go(base)}
            onEdit={editable ? () => go(`${base}/${openId}/edit`) : undefined}
            onDuplicate={editable ? () => navigate(`${base}/new${carryPeriod(search)}${carryPeriod(search) ? "&" : "?"}copy=${openId}`) : undefined}
            onArchive={editable ? () => opened.data && setArchiving(opened.data) : undefined}
          />
        </Suspense>
      ) : null}
      {creating || (editing && editable && opened.data) ? (
        <OfferEditor
          open
          offer={editing ? opened.data : undefined}
          seed={creating && copyFrom.data ? duplicateOffer(copyFrom.data, t("u6.ed.copySuffix")) : undefined}
          networks={networks}
          onClose={() => go(editing ? `${base}/${openId}` : base)}
          onSaved={(o) => go(`${base}/${o.id}`)}
          onArchive={editing && opened.data ? () => setArchiving(opened.data!) : undefined}
        />
      ) : null}
      <ConfirmModal
        open={!!archiving}
        onClose={() => setArchiving(null)}
        title={t("u6.archive.title", { name: archiving?.name ?? "" })}
        description={t("u6.archive.body")}
        confirmLabel={t("u6.common.archive")}
        cancelLabel={t("u6.common.cancel")}
        tone="danger"
        loading={archive.isPending}
        onConfirm={async () => {
          if (!archiving) return;
          try {
            await archive.mutateAsync(archiving.id);
            toast.success(t("u6.archive.done"));
            setArchiving(null);
            go(base);
          } catch (err) {
            toast.error(err instanceof Error ? err.message : t("u6.archive.failed"));
          }
        }}
      />
    </div>
  );
}

/**
 * The statuses a view lists when the status facet is empty — the same set
 * GetOfferLibrary counts for its tab, so the tab's number is the list's total.
 * «Работают» (and the two attention tiles, sets of ACTIVE offers) count status
 * ACTIVE; «Мои» counts every offer of the reader's that is not archived.
 */
function viewStatuses(v: Exclude<View, "archived">): OfferStatus[] {
  return v === "mine" ? [OfferStatus.ACTIVE, OfferStatus.PAUSED, OfferStatus.CAPPED] : [OfferStatus.ACTIVE];
}

function viewCount(v: View, lib: NonNullable<ReturnType<typeof useOfferLibrary>["data"]>): number | undefined {
  switch (v) {
    case "active":
      return Number(lib.active);
    case "capped":
      return Number(lib.cappedToday);
    case "silent":
      return Number(lib.withoutLeads7d);
    case "mine":
      return Number(lib.mine);
    case "archived":
      return undefined;
  }
}

function numberColumns(t: ReturnType<typeof useT>, byOffer: Map<string, OfferNumbers>): ColumnDef<Offer>[] {
  const n = (o: Offer) => statsRowToNumbers(byOffer.get(o.id)?.stats);
  const right = { align: "right" } as const;
  return [
    { id: "leads", header: t("u6.col.leads"), enableSorting: false, size: 76, meta: right, cell: ({ row }) => <span className="tabular-nums">{formatInt(n(row.original).leads || undefined)}</span> },
    {
      id: "approve",
      header: t("u6.col.approve"),
      enableSorting: false,
      size: 76,
      meta: right,
      cell: ({ row }) => <span className="tabular-nums text-muted-foreground">{formatPct(n(row.original).approve_rate, { digits: 0 })}</span>,
    },
    { id: "revenue", header: t("u6.col.revenue"), enableSorting: false, size: 90, meta: right, cell: ({ row }) => <span className="tabular-nums">{formatMoney(n(row.original).revenue || undefined)}</span> },
    {
      id: "campaigns",
      header: t("u6.col.campaigns"),
      enableSorting: false,
      size: 96,
      meta: right,
      cell: ({ row }) => <span className="tabular-nums text-muted-foreground">{byOffer.get(row.original.id)?.campaignCount || DASH}</span>,
    },
  ];
}

